import { z } from "zod";

export const channelPlatforms = [
  "wechat_official",
  "zhihu",
  "bilibili",
  "weibo",
  "community",
] as const;

export const channelPlatformSchema = z.enum(channelPlatforms);
export type ChannelPlatform = z.infer<typeof channelPlatformSchema>;

export const channelLabels: Record<ChannelPlatform, string> = {
  wechat_official: "公众号",
  zhihu: "知乎",
  bilibili: "B站",
  weibo: "微博",
  community: "社群",
};

export const channelProfileInputSchema = z.object({
  id: z.string().uuid().optional(),
  platform: channelPlatformSchema,
  displayName: z.string().trim().min(1).max(60),
  niche: z.string().trim().min(2).max(80),
  audienceSummary: z.string().trim().min(2).max(240),
  averageExposure: z.number().int().min(10).max(100_000_000),
  sampleBudgetCents: z.number().int().min(0).max(30_000).default(0),
  contentCostCents: z.number().int().min(0).max(100_000).default(0),
  officialComponentConfirmed: z.boolean().default(false),
});

export type ChannelProfileInput = z.infer<typeof channelProfileInputSchema>;

export type ChannelProfile = ChannelProfileInput & {
  id: string;
  createdAt: number;
  updatedAt: number;
};

export const outcomeStatusSchema = z.enum([
  "candidate",
  "published",
  "settled",
  "cancelled",
]);

export const outcomeInputSchema = z.object({
  id: z.string().uuid().optional(),
  handoffEventId: z.string().uuid(),
  status: outcomeStatusSchema,
  contentUrl: z.string().trim().url().max(500).nullable().optional(),
  publishedAt: z.number().int().positive().nullable().optional(),
  actualExposure: z.number().int().min(0).max(1_000_000_000).default(0),
  clicks: z.number().int().min(0).max(1_000_000_000).default(0),
  paidOrders: z.number().int().min(0).max(10_000_000).default(0),
  settledOrders: z.number().int().min(0).max(10_000_000).default(0),
  settledCommissionCents: z.number().int().min(0).max(1_000_000_000).default(0),
  sampleCostCents: z.number().int().min(0).max(1_000_000).default(0),
  contentCostCents: z.number().int().min(0).max(1_000_000).default(0),
}).superRefine((value, context) => {
  if (value.clicks > value.actualExposure) {
    context.addIssue({ code: "custom", path: ["clicks"], message: "点击数不能超过有效曝光。" });
  }
  if (value.paidOrders > value.clicks) {
    context.addIssue({ code: "custom", path: ["paidOrders"], message: "支付订单不能超过点击数。" });
  }
  if (value.settledOrders > value.paidOrders) {
    context.addIssue({ code: "custom", path: ["settledOrders"], message: "结算订单不能超过支付订单。" });
  }
});

export type OutcomeInput = z.infer<typeof outcomeInputSchema>;

export const subscriptionInterestSchema = z.object({
  plan: z.literal("founder"),
  contact: z.string().trim().min(2).max(120),
});

export type ScenarioName = "conservative" | "reference" | "optimistic";

export type EarningsScenario = {
  name: ScenarioName;
  clickRate: number;
  paymentConversionRate: number;
  invalidRate: number;
  expectedClicks: number;
  expectedPaidOrders: number;
  estimatedSettledCommissionCents: number;
  estimatedNetCents: number;
};

export type Recommendation = {
  productId: string;
  externalId: string;
  title: string;
  category: string;
  itemUrl: string | null;
  imageUrl: string | null;
  priceCents: number;
  couponCents: number;
  commissionRateBps: number;
  grossCommissionPerOrderCents: number;
  expectedSettledCommissionPerOrderCents: number;
  recentSales: number;
  sampleCostCents: number;
  dataOrigin: "synthetic_demo" | "taobao_official_api";
  observedAt: number;
  stale: boolean;
  confidence: "low" | "medium" | "high";
  score: number;
  fitReason: string;
  contentAngle: string;
  riskNote: string;
  disclosure: string;
  channelProfile: ChannelProfile;
  scenarios: EarningsScenario[];
};

export type Viewer = {
  user: {
    id: string;
    name: string;
    email: string;
    image: string | null;
  };
  subscription: {
    plan: "trial" | "founder";
    status: "trialing" | "active" | "expired";
    trialEndsAt: number;
    daysRemaining: number;
  };
};

